import { fromTheme, h3, styled, ValidationError, View } from 'lib_ui-primitives';
import PropTypes from 'prop-types';
import { cloneElement, createElement as rc, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useFormControl from '../../hooks/useFormControl';
import { default as DropDown } from '../_abstractComponent/DropDown';

import { constants } from 'lib_ui-services';
const { criteriaOperations } = constants;

import lodash from 'lodash';
const { isEqual } = lodash;

const CriteriaElementContainer = styled(View).attrs({ name: 'criteria-element' })`
    flex-grow: 0;
    flex-shrink: 0;
    flex-direction: column;
    padding: ${fromTheme('viewMargin')};
    padding-bottom: ${fromTheme('viewPaddingMore')};
    margin-bottom: ${fromTheme('viewMargin')};
    background-color: ${fromTheme('backgroundColorLighter')};
`;
CriteriaElementContainer.displayName = 'CriteriaElement';

const _p = {
    DropDown,
    useFormControl
};
export const _private = _p;

const EMPTY_OBJECT = {};
function CriteriaElement(props) {
    const {
        hNode: { id, treePosition },
        supportedOperations = [criteriaOperations.EQUALS, criteriaOperations.NOT_EQUALS],
        defaultValue,
        children
    } = props || { hNode: {} };

    const defaultOp = supportedOperations[0];
    const [opValue, setOpValue] = useState(defaultOp);
    const [innerValue, setInnerValue] = useState(defaultValue);
    const preventStaleRehydrateRef = useRef(false);
    const innerValueRef = useRef(innerValue);
    innerValueRef.current = innerValue;

    const {
        autoFocus,
        value: combinedValue,
        setValue: setCombinedValue,
        onBlur,
        onFocus,
        defaultReady,
        disabled,
        errors,
        active
    } = _p.useFormControl({ ...props, defaultValue: EMPTY_OBJECT });

    const createNewCombinedValue = useCallback(
        function createNewCombinedValue(newOp, newInnerValue) {
            if (newOp != null) {
                if (isEqual(newOp, opValue)) return;
                setOpValue(newOp);
            } else {
                newOp = opValue;
            }

            // Some clear paths emit undefined/null instead of an empty array.
            // For array-backed criteria values, treat that as an explicit clear.
            if (newInnerValue == null && Array.isArray(innerValue)) {
                newInnerValue = [];
            }

            if (newInnerValue != null) {
                if (typeof newInnerValue === 'object' && newInnerValue.title) {
                    // quick and (somewhat?) dirty fix.
                    // CriteriaElement probably should receive a "formatInnerValue()" function or something like that
                    // so that the dropdown can implement logic to pull all the proper fields (not just "title") based on its configuration.
                    // alternatively, it might be the case that the setValue as it is implemented needs to be re-evaluated,
                    // as perhaps IT should already clean up the result before ever sending it over?
                    // Then again, the backend seems to only be matching on _id and title.... so this might be enough permanently
                    // alternatively, we might need to fix the backend to just look for _ids?

                    const { _id, title } = newInnerValue;
                    newInnerValue = { _id, title };
                }
                const isClearOperation = Array.isArray(newInnerValue) && newInnerValue.length === 0;
                const isAlreadyCleared = Array.isArray(innerValue) && innerValue.length === 0;
                if (isClearOperation && isAlreadyCleared) {
                    return;
                }
                if (!isClearOperation) {
                    if (isEqual(newInnerValue, innerValue)) {
                        return;
                    }
                    preventStaleRehydrateRef.current = false;
                } else {
                    // Clear operations can trigger a stale non-empty merge from form/rules-engine.
                    // Keep updating combined value, but mark next non-empty sync value as ignorable.
                    preventStaleRehydrateRef.current = true;
                }
                // Keep the ref in sync immediately so stale rehydrate checks are reliable
                // even before React applies state updates.
                innerValueRef.current = newInnerValue;
                setInnerValue(newInnerValue);
            } else {
                newInnerValue = innerValue;
            }
            const newCombinedValue = {
                [newOp._id]: newInnerValue
            };
            setCombinedValue(prev => (isEqual(prev, newCombinedValue) ? prev : newCombinedValue));
        },
        [innerValue, opValue, setCombinedValue]
    );

    useEffect(() => {
        if (combinedValue && Object.keys(combinedValue) && !isEqual(combinedValue, EMPTY_OBJECT)) {
            const newOpId = Object.keys(combinedValue)[0];
            const newOp = criteriaOperations.find(newOpId);
            const newValue = combinedValue[newOpId];

            const isStaleRehydrateAfterClear =
                preventStaleRehydrateRef.current &&
                Array.isArray(innerValueRef.current) &&
                innerValueRef.current.length === 0 &&
                Array.isArray(newValue) &&
                newValue.length > 0;

            if (isStaleRehydrateAfterClear) {
                // Re-assert the cleared state so form data and UI remain consistent.
                const clearCombinedValue = { [newOpId]: [] };
                setCombinedValue(prev => (isEqual(prev, clearCombinedValue) ? prev : clearCombinedValue));
                return;
            }

            if (Array.isArray(newValue) && newValue.length === 0) {
                preventStaleRehydrateRef.current = true;
            } else {
                preventStaleRehydrateRef.current = false;
            }

            setInnerValue(prev => (isEqual(prev, newValue) ? prev : newValue));
            setOpValue(prev => (isEqual(prev, newOp) ? prev : newOp));
        }
    }, [setInnerValue, setOpValue, combinedValue]);

    //We are letting React render the nested elements first, so we don't need any logic (or references) to that
    //then, we clone, inject with our propertyPath, memoize and replace those elements.
    //(The memoization is so that on subsequent renders, our cloned and updated element is directly rendered.)
    const subControls = useMemo(
        () =>
            [].concat(children).map(element => {
                return cloneElement(element, {
                    ...element.props,
                    autoFocus,
                    disabled,
                    value: innerValue,
                    required: true,
                    //when coming from an input field like shortText of integer:
                    onChange: x => createNewCombinedValue(undefined, x),
                    //when coming from a dropdown related component
                    setValue: x => createNewCombinedValue(undefined, x),
                    //when coming from cascade clear on multi-picker dropdowns
                    removeAllSelected: () => createNewCombinedValue(undefined, []),
                    onBlur,
                    onFocus,
                    active
                });
            }),
        [autoFocus, children, createNewCombinedValue, disabled, innerValue, onBlur, onFocus, active]
    );

    const opProps = useMemo(
        () => ({
            hNode: {
                id: `op-${id}`,
                displayUnassignedRow: false,
                treePosition
            },
            autoFocus,
            required: true,
            disabled,
            setValue: x => createNewCombinedValue(x),
            value: opValue,
            onBlur,
            onFocus,
            records: supportedOperations,
            active
        }),
        [
            autoFocus,
            disabled,
            id,
            onBlur,
            onFocus,
            opValue,
            supportedOperations,
            treePosition,
            createNewCombinedValue,
            active
        ]
    );

    if (!defaultReady) {
        return rc(h3, null, 'Loading...');
    }

    return rc(
        CriteriaElementContainer,
        { id },
        rc(_p.DropDown, opProps),
        ...subControls,
        rc(ValidationError, { errors })
    );
}

CriteriaElement.propTypes = {
    hNode: PropTypes.shape({
        treePosition: PropTypes.shape({
            sequence: PropTypes.number.isRequired
        }).isRequired
    }).isRequired,
    supportedOperations: PropTypes.arrayOf(
        PropTypes.shape({
            _id: PropTypes.string,
            title: PropTypes.string,
            symbol: PropTypes.string
        })
    ),
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.element), PropTypes.element]).isRequired
};
export default CriteriaElement;
